import React, { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { TextField } from '@yoast/ui-library';
import {
  getGetBrandsQueryKey,
  getGetCurrentUserQueryKey,
  useDeleteBrand,
} from '../api/generated/insights';
import { useToast } from '../hooks';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface DeleteBrandModalProps {
  isOpen: boolean;
  brandId: string;
  onClose: () => void;
  brandName?: string;
}

const DeleteBrandModal: React.FC<DeleteBrandModalProps> = ({
  isOpen,
  brandId,
  onClose,
  brandName,
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [confirmationValue, setConfirmationValue] = useState('');

  const { mutate: deleteBrand, isPending } = useDeleteBrand({
    mutation: {
      onSuccess: async () => {
        // Invalidate or refetch queries related to brands and user data to update the license usage counts.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetBrandsQueryKey() }),
          queryClient.invalidateQueries({
            queryKey: getGetCurrentUserQueryKey(),
          }),
        ]);

        toast('Brand deleted successfully', {
          variant: 'success',
        });

        // After successful deletion:
        onClose();
      },
      onError: (error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : 'Please try again later.';
        toast(`Failed to delete brand. ${errorMessage}`, {
          variant: 'error',
        });
      },
    },
  });

  useEffect(() => {
    if (isOpen) {
      setConfirmationValue(''); // Reset confirmation input when modal opens
    }
  }, [isOpen]);

  return (
    <DeleteConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={() => {
        if (!brandName) {
          console.error('Brand name is required for deletion confirmation');
          return;
        }
        deleteBrand({ brandId });
      }}
      isLoading={isPending}
      disabled={confirmationValue.toLowerCase() !== brandName?.toLowerCase()}
      title={'Delete brand?'}
      description={
        <>
          <p>
            This will permanently delete all data associated with this brand,
            including past analyses and query history.
          </p>
          <p>This action cannot be undone.</p>

          <TextField
            id="delete-modal-brand-name-confirmation"
            label={`To confirm, please type your brand name ${brandName}:`}
            value={confirmationValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setConfirmationValue(e.target.value)
            }
          />
          <p>
            <em>
              This is to make sure you understand this action is permanent.
            </em>
          </p>
        </>
      }
      confirmText="Yes, delete brand"
      cancelText="No, go back"
    />
  );
};

export default DeleteBrandModal;
